import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    FaMicrophone, FaSearch, FaRedo, FaUser, FaEnvelope, FaPhone,
    FaMapMarkerAlt, FaWallet, FaCalendarAlt, FaIdCard, FaTimes,
    FaBuilding, FaCheckCircle, FaClock, FaChevronLeft, FaChevronRight,
    FaEye, FaFilter, FaNewspaper, FaAward
} from "react-icons/fa";

const PressConferenceUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMedia, setFilterMedia] = useState("All");
    const [selectedUser, setSelectedUser] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/admin/press-conference/users`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            const data = response.data.data || [];
            setUsers(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to fetch press users");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterMedia]);

    const mediaTypes = ["All", "Print", "Digital", "TV", "Radio", "Online", "Other"];

    const mediaConfig = {
        Print: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
        Digital: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
        TV: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
        Radio: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
        Online: { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-200" },
        Other: { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" },
        All: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
    };

    const filteredUsers = users
        .filter((u) => {
            const term = searchTerm.toLowerCase();
            const matchSearch =
                (u.name && u.name.toLowerCase().includes(term)) ||
                (u.email && u.email.toLowerCase().includes(term)) ||
                (u.mobile && u.mobile.toLowerCase().includes(term)) ||
                (u.pressConferenceId && u.pressConferenceId.toLowerCase().includes(term)) ||
                (u.organization && u.organization.toLowerCase().includes(term)) ||
                (u.city && u.city.toLowerCase().includes(term)) ||
                (u.state && u.state.toLowerCase().includes(term));

            const matchMedia = filterMedia === "All" || u.mediaType === filterMedia;
            return matchSearch && matchMedia;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    const getInitials = (name) => {
        if (!name) return "P";
        const parts = name.trim().split(" ");
        return parts.length >= 2
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : name[0].toUpperCase();
    };

    const avatarColors = [
        "from-green-500 to-teal-600",
        "from-violet-500 to-purple-700",
        "from-blue-500 to-indigo-700",
        "from-amber-500 to-orange-600",
        "from-rose-500 to-pink-700",
        "from-cyan-500 to-sky-700",
    ];

    const getColor = (id) => avatarColors[id ? id.charCodeAt(id.length - 1) % avatarColors.length : 0];

    return (
        <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans">
            {/* Top accent bar */}
            <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-teal-500 to-emerald-600 z-50" />

            {/* Page Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-700 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-green-500/30 ring-4 ring-green-50">
                        <FaMicrophone />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black text-[#1e293b] tracking-tight">
                            Press Organizers
                        </h1>
                        <p className="text-[#94a3b8] text-xs font-bold tracking-widest mt-0.5 uppercase">
                            {users.length} registered press conference accounts
                        </p>
                    </div>
                </div>
                <button
                    onClick={fetchUsers}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 transition-all font-bold text-xs active:scale-95"
                >
                    <FaRedo className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {[
                    { label: "Total Organizers", value: users.length, color: "from-green-500 to-teal-600", icon: <FaUser /> },
                    { label: "Total Conferences", value: users.reduce((s, u) => s + (u.totalSubmissions || 0), 0), color: "from-blue-500 to-indigo-600", icon: <FaNewspaper /> },
                    { label: "Completed", value: users.reduce((s, u) => s + (u.completedSubmissions || 0), 0), color: "from-emerald-500 to-green-700", icon: <FaCheckCircle /> },
                    { label: "Total Wallet", value: `₹${users.reduce((s, u) => s + (u.walletBalance || 0), 0).toFixed(0)}`, color: "from-amber-500 to-orange-600", icon: <FaWallet /> },
                ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-[#f1f5f9] shadow-sm flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} text-white flex items-center justify-center shadow-md text-sm`}>
                            {stat.icon}
                        </div>
                        <div>
                            <div className="text-xl font-black text-[#1e293b]">{stat.value}</div>
                            <div className="text-[9px] font-black uppercase tracking-widest text-[#94a3b8]">{stat.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Card */}
            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-green-500/5 border border-[#f1f5f9] overflow-hidden">
                {/* Toolbar */}
                <div className="p-5 md:p-8 border-b border-[#f1f5f9] flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-500/30">
                            <FaFilter />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-[#1e293b]">Directory</h3>
                            <p className="text-xs text-[#94a3b8] font-bold">{filteredUsers.length} organizers listed</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row w-full lg:w-auto gap-3">
                        {/* Search */}
                        <div className="relative group w-full sm:w-72">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-green-500 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search by name, ID, org..."
                                className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-green-500/10 focus:bg-white focus:border-green-200 transition-all text-sm font-medium text-[#334155]"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Media Filter */}
                        <select
                            value={filterMedia}
                            onChange={(e) => setFilterMedia(e.target.value)}
                            className="px-4 py-3 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-green-500/10 text-sm font-bold text-[#334155] cursor-pointer"
                        >
                            {mediaTypes.map((m) => (
                                <option key={m} value={m}>{m === "All" ? "All Media Types" : m}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc]">
                                <th className="px-5 py-4 text-left text-[9px] font-black text-[#64748b] uppercase tracking-widest">#</th>
                                <th className="px-5 py-4 text-left text-[9px] font-black text-[#64748b] uppercase tracking-widest">Organizer</th>
                                <th className="px-5 py-4 text-left text-[9px] font-black text-[#64748b] uppercase tracking-widest">Identity / Media</th>
                                <th className="px-5 py-4 text-left text-[9px] font-black text-[#64748b] uppercase tracking-widest">Contact</th>
                                <th className="px-5 py-4 text-left text-[9px] font-black text-[#64748b] uppercase tracking-widest">Conferences</th>
                                <th className="px-5 py-4 text-left text-[9px] font-black text-[#64748b] uppercase tracking-widest">Wallet</th>
                                <th className="px-5 py-4 text-center text-[9px] font-black text-[#64748b] uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f1f5f9]">
                            {loading ? (
                                Array(6).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="7" className="px-5 py-5">
                                            <div className="h-10 bg-gray-100 rounded-2xl w-full" />
                                        </td>
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan="7" className="px-5 py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-400 text-2xl">
                                                <FaTimes />
                                            </div>
                                            <p className="text-red-500 font-bold text-sm">{error}</p>
                                            <button onClick={fetchUsers} className="px-4 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-black hover:bg-red-100 transition-all">
                                                Retry
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-5 py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 text-3xl">
                                                <FaSearch />
                                            </div>
                                            <p className="text-[#94a3b8] font-bold">No press organizers match your criteria</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((user, idx) => {
                                    const mc = mediaConfig[user.mediaType] || mediaConfig.Other;
                                    return (
                                        <tr key={user._id} className="hover:bg-[#f8fafc] transition-all duration-150 group">
                                            {/* # */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <span className="text-[10px] font-black text-gray-400">
                                                    {(currentPage - 1) * itemsPerPage + idx + 1}
                                                </span>
                                            </td>

                                            {/* Organizer */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getColor(user._id)} flex items-center justify-center text-white text-sm font-black shadow-md flex-shrink-0`}>
                                                        {getInitials(user.name)}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-[#1e293b] truncate max-w-[140px]">{user.name || "N/A"}</p>
                                                        <p className="text-[9px] text-[#94a3b8] font-black uppercase tracking-widest">
                                                            {user.pressConferenceId || "—"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Identity / Media */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <FaBuilding className="text-gray-300 text-[9px]" />
                                                        <span className="text-xs font-bold text-[#475569] truncate max-w-[120px]">{user.organization || "—"}</span>
                                                    </div>
                                                    {user.mediaType && (
                                                        <span className={`inline-block px-2 py-0.5 text-[8px] font-black uppercase tracking-widest rounded-lg border ${mc.bg} ${mc.text} ${mc.border} w-fit`}>
                                                            {user.mediaType}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Contact */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <FaEnvelope className="text-gray-300 text-[9px]" />
                                                        <span className="text-xs font-medium text-[#475569] truncate max-w-[160px]">{user.email || "—"}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <FaPhone className="text-gray-300 text-[9px]" />
                                                        <span className="text-xs font-medium text-[#94a3b8]">{user.mobile || "—"}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Conferences */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex flex-col items-center">
                                                        <span className="text-sm font-black text-[#1e293b]">{user.totalSubmissions || 0}</span>
                                                        <span className="text-[8px] text-gray-400 font-black uppercase">Total</span>
                                                    </div>
                                                    <div className="w-px h-8 bg-gray-100" />
                                                    <div className="flex flex-col items-center">
                                                        <span className="text-sm font-black text-emerald-600">{user.completedSubmissions || 0}</span>
                                                        <span className="text-[8px] text-gray-400 font-black uppercase">Done</span>
                                                    </div>
                                                    <div className="flex flex-col items-center">
                                                        <span className="text-sm font-black text-amber-500">{user.pendingSubmissions || 0}</span>
                                                        <span className="text-[8px] text-gray-400 font-black uppercase">Pending</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Wallet */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center text-[10px]">
                                                        <FaWallet />
                                                    </div>
                                                    <span className="text-sm font-black text-[#1e293b]">₹{(user.walletBalance || 0).toFixed(2)}</span>
                                                </div>
                                            </td>

                                            {/* Actions */}
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex items-center justify-center">
                                                    <button
                                                        onClick={() => setSelectedUser(user)}
                                                        className="w-8 h-8 flex items-center justify-center bg-green-50 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-all border border-green-100"
                                                        title="View Details"
                                                    >
                                                        <FaEye size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="p-6 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-sm text-[#64748b] font-bold">
                            Showing <span className="text-[#1e293b]">{indexOfFirstItem + 1}</span> – <span className="text-[#1e293b]">{Math.min(indexOfLastItem, filteredUsers.length)}</span> of <span className="text-[#1e293b] font-black">{filteredUsers.length}</span> organizers
                        </p>
                        <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-[#e2e8f0] shadow-sm">
                            <button
                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                disabled={currentPage === 1}
                                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-green-50 hover:text-green-600 disabled:opacity-30 transition-all rounded-xl"
                            >
                                <FaChevronLeft size={12} />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                .map((p, i, arr) => (
                                    <React.Fragment key={p}>
                                        {i > 0 && arr[i - 1] !== p - 1 && <span className="text-gray-300 text-xs">…</span>}
                                        <button
                                            onClick={() => setCurrentPage(p)}
                                            className={`w-10 h-10 flex items-center justify-center rounded-xl text-xs font-black transition-all ${currentPage === p
                                                ? "bg-green-600 text-white shadow-lg shadow-green-500/20 scale-110"
                                                : "text-gray-500 hover:bg-gray-50 hover:text-green-600"
                                                }`}
                                        >
                                            {p}
                                        </button>
                                    </React.Fragment>
                                ))}
                            <button
                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-green-50 hover:text-green-600 disabled:opacity-30 transition-all rounded-xl"
                            >
                                <FaChevronRight size={12} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedUser && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0f172a]/70 backdrop-blur-lg"
                    onClick={() => setSelectedUser(null)}
                >
                    <div
                        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="relative h-36 bg-gradient-to-br from-green-500 via-teal-600 to-emerald-800 flex items-end p-6">
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="absolute top-4 right-4 w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center text-white backdrop-blur-sm transition-all"
                            >
                                <FaTimes />
                            </button>
                            <div className="flex items-center gap-4">
                                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${getColor(selectedUser._id)} flex items-center justify-center text-white text-2xl font-black shadow-2xl border-4 border-white/20`}>
                                    {getInitials(selectedUser.name)}
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-white tracking-tight">{selectedUser.name}</h2>
                                    <p className="text-white/70 text-xs font-bold uppercase tracking-widest mt-0.5">
                                        {selectedUser.pressConferenceId || "No Press ID"}
                                    </p>
                                    {selectedUser.mediaType && (
                                        <span className="mt-1 inline-block px-2 py-0.5 bg-white/20 text-white text-[9px] font-black uppercase tracking-widest rounded-lg border border-white/20">
                                            {selectedUser.mediaType}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 max-h-[65vh] overflow-y-auto">
                            {/* Conference Stats */}
                            <div className="grid grid-cols-4 gap-3 mb-6">
                                {[
                                    { label: "Total", value: selectedUser.totalSubmissions || 0, color: "text-blue-600", bg: "bg-blue-50" },
                                    { label: "Pending", value: selectedUser.pendingSubmissions || 0, color: "text-amber-600", bg: "bg-amber-50" },
                                    { label: "Approved", value: selectedUser.approvedSubmissions || 0, color: "text-green-600", bg: "bg-green-50" },
                                    { label: "Completed", value: selectedUser.completedSubmissions || 0, color: "text-emerald-700", bg: "bg-emerald-50" },
                                ].map((s, i) => (
                                    <div key={i} className={`${s.bg} rounded-2xl p-3 text-center`}>
                                        <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 mt-0.5">{s.label}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {[
                                    { icon: <FaEnvelope />, label: "Email", value: selectedUser.email },
                                    { icon: <FaPhone />, label: "Mobile", value: selectedUser.mobile },
                                    { icon: <FaBuilding />, label: "Organization", value: selectedUser.organization },
                                    { icon: <FaAward />, label: "Designation", value: selectedUser.designation },
                                    { icon: <FaMapMarkerAlt />, label: "City", value: selectedUser.city },
                                    { icon: <FaMapMarkerAlt />, label: "State", value: selectedUser.state },
                                    { icon: <FaIdCard />, label: "Aadhar No.", value: selectedUser.aadharNo },
                                    { icon: <FaIdCard />, label: "PAN Card", value: selectedUser.pancard },
                                    { icon: <FaWallet />, label: "Wallet Balance", value: `₹${(selectedUser.walletBalance || 0).toFixed(2)}` },
                                    { icon: <FaCalendarAlt />, label: "Registered On", value: selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—" },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 bg-[#f8fafc] rounded-xl">
                                        <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center text-green-600 text-xs shadow-sm flex-shrink-0 mt-0.5">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-widest text-[#94a3b8]">{item.label}</p>
                                            <p className="text-sm font-bold text-[#1e293b] mt-0.5">{item.value || "—"}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Address */}
                            {selectedUser.residenceaddress && (
                                <div className="mt-4 flex items-start gap-3 p-3 bg-[#f8fafc] rounded-xl">
                                    <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center text-green-600 text-xs shadow-sm flex-shrink-0 mt-0.5">
                                        <FaMapMarkerAlt />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-[#94a3b8]">Residence Address</p>
                                        <p className="text-sm font-bold text-[#1e293b] mt-0.5">{selectedUser.residenceaddress}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-[#f8fafc] border-t border-[#f1f5f9] flex justify-end">
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-lg shadow-green-500/25"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PressConferenceUsers;
